/**
 * `src/lib/ui/nav/links.ts` — every internal href in the reader, built once.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY THESE GO THROUGH `resolve()` AND NOT A TEMPLATE LITERAL
 * ═══════════════════════════════════════════════════════════════════════════
 * `resolve()` applies `config.kit.paths.base`. A hand-built `/read/2` is correct
 * only while the app is served from the root of its origin, and SPEC §1.4 keeps
 * the host deliberately swappable — "GitHub Pages is a verified fallback" —
 * where a project site lives under `/<repo>/`. `eslint-plugin-svelte`'s
 * `no-navigation-without-resolve` rule is what enforces it; this module is
 * where the enforcement is satisfied once instead of at nine call sites.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  AND WHY THE PARAMLESS CALL CARRIES AN EXPLICIT TYPE ARGUMENT
 * ═══════════════════════════════════════════════════════════════════════════
 * `resolve()`'s parameter is a rest tuple, `ResolveArgs<T>`, that is `[route]`
 * for a route with no parameters and `[route, params]` for one with them. For
 * `'/read'` the inference site is ambiguous — the literal satisfies BOTH the
 * `RouteId` union and the `Pathname` union that Kit generates, so `T` widens to
 * the constraint, `ResolveArgs` distributes over it, and TypeScript demands the
 * widest arity it finds: "Expected 2 arguments, but got 1".
 *
 * `resolve<'/read'>('/read')` pins `T` to the single literal and the tuple
 * collapses to `[route]`. It is a type-checker workaround and it is written down
 * here rather than repeated, unexplained, in three components.
 */

import { resolve } from '$app/paths';

/**
 * The surah index.
 *
 * A function and not a `const`: `resolve()` reads `paths.base`, which SvelteKit
 * assigns during start-up. A module-level constant would capture whatever the
 * value was at import time, which is a bug that only appears on a host with a
 * base path — i.e. never in development and always in production.
 */
export function readIndexHref(): string {
	return resolve('/read', {});
}

/** One surah, optionally at a given ayah. */
export function surahHref(surah: number, ayah?: number): string {
	const path = resolve('/read/[surah]', { surah: String(surah) });
	return ayah === undefined ? path : `${path}?ayah=${ayah}`;
}

/** `/bookmarks` — every ayah marked with «علامة». The tab bar's last tab. */
export function bookmarksHref(): string {
	return resolve('/bookmarks', {});
}

/** Where the Read tab goes: the continue point when there is one, the index of
 *  the mushaf from the top when there is not. */
export function continueHref(position: { surah: number; ayah: number } | null): string {
	return position ? surahHref(position.surah, position.ayah) : surahHref(1);
}

/** `/salah` — prayer times, the third tab. */
export function salahHref(): string {
	return resolve('/salah', {});
}

/** `/` — home: the hub (D12). The tools that used to sit behind «المزيد» are
 *  tiles here, and Settings is the action in its app bar. */
export function homeHref(): string {
	return resolve('/', {});
}

/** `/settings`, reached from the settings action in home's app bar. */
export function settingsHref(): string {
	return resolve('/settings', {});
}

/** `/search` — Qur'an full-text search, optionally with a query filled in. */
export function searchHref(query?: string): string {
	const path = resolve('/search', {});
	return query ? `${path}?q=${encodeURIComponent(query)}` : path;
}
